/**
 * Orchestrates the edit-mode save flow for the assessment editor
 * (Ticket 7 / Task #53). The day-to-day admin path is PATCH
 * /assessments/{id} followed by a *conditional* PUT
 * /assessments/{id}/teams. The conditions are subtle enough that the
 * view layer was hard to test directly, so we mirror the
 * ``createAssessmentWithTeams`` pattern: a pure helper that the view
 * delegates to and that unit tests can exercise without React.
 *
 * Rules (mirroring the inline comment in ``AssessmentEditView``'s
 * ``performSave`` edit branch):
 *   - PATCH always runs first. Any error it throws propagates — the
 *     caller's existing ``updateAssessment`` ``onError`` owns that
 *     surface.
 *   - The team PUT is skipped when the team set is unchanged
 *     (``teamsChanged === false``). The server would just no-op but
 *     skipping avoids an unnecessary round-trip *and* avoids a 422
 *     when the PATCH happened to revert the assessment to Draft.
 *   - The team PUT is skipped when the PATCH reverted the assessment
 *     to Draft (``updated.status !== "saved"``). Teams can't be
 *     assigned to a Draft assessment — the server clears the
 *     junction on revert, and a PUT would 422.
 *   - On a successful PUT the helper returns ``persistedTeamIds`` so
 *     the caller can keep its local "initial" set in sync. Without
 *     that sync a second Save with no further edits would re-PUT.
 *   - A PUT failure after a successful PATCH surfaces as
 *     ``teamAssignmentError``. The assessment was still updated on
 *     the server, so the caller keeps the ``updated`` result and can
 *     surface the error inline for retry.
 */
import type { AssessmentDetail, AssessmentInput, AssessmentTeamRef } from "./api";

export type EditAssessmentWithTeamsDeps = {
  updateAssessment: (
    body: AssessmentInput,
  ) => Promise<AssessmentDetail & { unassigned_teams?: AssessmentTeamRef[] }>;
  setAssessmentTeams: (
    assessmentId: string,
    teamIds: string[],
  ) => Promise<AssessmentTeamRef[]>;
};

export type EditAssessmentWithTeamsResult = {
  updated: AssessmentDetail & { unassigned_teams?: AssessmentTeamRef[] };
  teamPutCalled: boolean;
  /** Ids returned by the PUT, for the caller to sync its initial set. */
  persistedTeamIds: string[] | null;
  teamAssignmentError: string | null;
};

export async function editAssessmentWithTeams(
  body: AssessmentInput,
  selectedTeamIds: string[],
  teamsChanged: boolean,
  deps: EditAssessmentWithTeamsDeps,
): Promise<EditAssessmentWithTeamsResult> {
  const updated = await deps.updateAssessment(body);
  if (!teamsChanged || updated.status !== "saved") {
    return {
      updated,
      teamPutCalled: false,
      persistedTeamIds: null,
      teamAssignmentError: null,
    };
  }
  try {
    const teams = await deps.setAssessmentTeams(updated.id, selectedTeamIds);
    return {
      updated,
      teamPutCalled: true,
      persistedTeamIds: teams.map((t) => t.id),
      teamAssignmentError: null,
    };
  } catch (e) {
    const teamAssignmentError =
      (e as { message?: string })?.message || "Failed to update teams";
    return {
      updated,
      teamPutCalled: true,
      persistedTeamIds: null,
      teamAssignmentError,
    };
  }
}
